import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getCrmBrandLocationsEndpointIdentifier } from './use-get-crm-brand-locations-api';

const endpoint = 'ums/v1/crm/management/brands/locations';
export const updateCrmBrandLocationEndpointIdentifier = endpoint;

interface BodyType {
  name?: string;
  address?: string;
  geoLocationId?: number;
  landlineNumber?: string;
  type?: 'physical_location' | 'cloud_kitchen';
  area?: string;
}

type TData = Record<string, any>;
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, { id: number; data: BodyType }>;
type ReturnType = UseMutationResult<TData, TError, { id: number; data: BodyType }>;

export function useUpdateCrmBrandLocationApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, data }: { id: number; data: BodyType }) => {
    try {
      const response = await http.put(`${endpoint}/${id}`, data);

      toast({
        description: 'Brand location updated successfully',
        status: 'success',
      });

      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries([getCrmBrandLocationsEndpointIdentifier]);
    },
    ...otherOptions,
  });
}
